/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2017, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'jquery',
	'underscore',
	'./QueryPostProcessor',
	'../../../../widgets/livewidget/nls/StringResources',
	'../../../../widgets/livewidget/query/QueryResultDataUtils',
	'../../../../apiHelpers/SlotAPIHelper'
], ($, _, QueryPostProcessor, StringResources, QueryResultDataUtils, SlotAPIHelper) => {

	/**
	 * This Class does Query result response post-processing such as processing the original query result
	 * into a data re-structure to support multiple measures as series.
	 **/
	class PostProcessMeasuresAsSeries extends QueryPostProcessor{
		/**
		 * @Constructor
		 * @param {Object} options
		 */
		constructor(options) {
			super(options);
			this.visualization = options.visualization;
			this.queryDefinition = options.queryDefinition;
		}

		/**
		 * Process Query result based on the passed options to constructor.
		 * For instance, limit the rows of datapoints so that in the response, for
		 * specified dataitem, 'product', there will be only three tuples returned
		 *
		 * @return {QueryResultData}
		 */
		_processData() {
			if (this._queryResultData && this._queryResultData.data && this._canProcess()) {
				const result = this._categorizeDataItems();
				// post-process the data items
				this._processDataItems(result);

				// post-process the data points
				this._processDataPoints(result);
			}
			//Process Done
			return this._queryResultData;
		}

		/**
		 * Determine whether multiple measure series is supported
		 */
		_canProcess() {
			const slots = this._getDatasetSlotList();
			let hasMultiMeasuresSeries = false, hasMultiMeasuresVal = false;

			_.each(slots, function(slot) {
				hasMultiMeasuresSeries = hasMultiMeasuresSeries || SlotAPIHelper.isMultiMeasuresSeriesSlot(slot);
				hasMultiMeasuresVal = hasMultiMeasuresVal || SlotAPIHelper.isMultiMeasuresValueSlot(slot);
			});

			return hasMultiMeasuresSeries && hasMultiMeasuresVal;
		}

		/**
		 *  Get the mapped projectionId for each dataItem.
		 */
		_getQueryProjectionId(slot, dataItemIdx) {
			// We don't project MultiMeasureSeries dataItem
			if (SlotAPIHelper.isMultiMeasuresSeriesOrValue(slot, dataItemIdx)) {
				return null;
			}
			const dataItemList = slot.getDataItemList();
			const validDataItemIds = _.filter(dataItemList, (dataItem, idx) => {
				return  !SlotAPIHelper.isMultiMeasuresSeriesOrValue(slot, idx);
			});

			if (slot.isStacked() && validDataItemIds.length > 1) {
				return slot.getId();
			}
			return dataItemList[dataItemIdx].getId();
		}

		/**
		 * Categorize the existing data items by category and ordinals
		 * Keep track of data items original index and also the slot Id it which the data item is mapped.
		 * The original index later allows the data points (prior to restructure) to access the mapping slot and data item.
		 *
		 * Despite the result data being the original data, the slot mapping already includes the multi-measures series mapping.
		 * The slot mapping for mult-measures series needs to be ignored at this stage in order to match with the original data.
		 */
		_categorizeDataItems() {
			let result = {
				cats: [],
				ords: [],
				commonIndices: {},
				mappedIndices: {},
				extraDataItems:[]
			};

			const resultQueryDataItems = this._queryResultData.dataItems;
			const allProjectionIds = _.map(resultQueryDataItems, dataItem => dataItem.itemClass.id);
			const mappedProjectionIds = [];
			const slotList = this._getDatasetSlotList();

			for (let i = 0; i < slotList.length; i++) {
				const slot = slotList[i];
				const dataItemList = slot.getDataItemList();
				for (let dataItemIdx = 0; dataItemIdx < dataItemList.length; dataItemIdx++) {
					let queryProjectionId = this._getQueryProjectionId(slot, dataItemIdx);
					if (queryProjectionId) {
						const type = dataItemList[dataItemIdx].getType();
						const idx = allProjectionIds.indexOf(queryProjectionId);
						const newEntry = {
							item: idx !== -1 ? resultQueryDataItems[idx] : undefined,
							slotId: slot.getId(),
							index: idx !== -1 ? idx : undefined
						};
						if (newEntry.index !== undefined) {
							result.mappedIndices[newEntry.index] = true;
							if (type === 'fact' && !slot.getDefinition().isMultiMeasureSupported()) {
								result.commonIndices[newEntry.index] = true;
							}
						}
						(type === 'attribute'  ? result.cats : result.ords).push(newEntry);
						mappedProjectionIds.push(queryProjectionId);
					}
				}
			}

			const extraDataItemIds = _.difference(allProjectionIds, mappedProjectionIds);
			resultQueryDataItems.forEach((dataItem, index) => {
				if(extraDataItemIds.indexOf(dataItem.itemClass.id) !== -1){
					result.commonIndices[index] = true;
					result.extraDataItems.push({ dataItem });
				}
			} );
			return result;
		}

		/**
		 * Process the data items
		 * Consolidate the multiple measures to one measure data item and a extra series to indicate the measure
		 */
		_processDataItems(dataItems) {
			const resultData = this._queryResultData;
			const slots = this._getDatasetSlotList();

			// rebuild the dataItems from scratch!
			resultData.dataItems.length = 0;

			// maintain the order based on slots
			for(let i = 0; i< slots.length; i++) {
				const slot = slots[i];
				let dataItem;
				let isCategory = slot.getDataItemList()[0].getType() === 'attribute';

				if (isCategory) {
					if (SlotAPIHelper.isMultiMeasuresSeriesSlot(slot)) {
						let measures = _.map(dataItems.ords, (ordinal) => {
							const slotDefinition = this.visualization.getDefinition().getSlot(ordinal.slotId);
							const supportsMultiMeasure = slotDefinition.getProperty('multiMeasure');
							if (ordinal && ordinal.item && ordinal.item.itemClass && supportsMultiMeasure) {
								return ordinal.item.itemClass.h[0];
							}
							return undefined;
						});
						measures = _.compact(measures);
						// apply the multiple measures as a series
						dataItem = this._getSeriesDataItemforMultipleMeasures(dataItems.cats, measures, slot);
					} else {
						dataItem = this._getDataItem(dataItems.cats, slot);
					}
				} else if (SlotAPIHelper.isMultiMeasuresValueSlot(slot)) {
					// apply the value for all measures
					dataItem = this._getValueDataItemForMultipleMeasures(slot);
				} else {
					const matchDataItems = this._getDataItem(dataItems.ords, slot, /*findAll*/ true);
					resultData.dataItems.push.apply(resultData.dataItems, matchDataItems);
				}

				if (dataItem) {
					resultData.dataItems.push(dataItem);
				}
			}
			dataItems.extraDataItems.forEach(extraDataItem => {
				extraDataItem.index = resultData.dataItems.length;
				resultData.dataItems.push(extraDataItem.dataItem);
			});
		}

		/**
		 * Find the data item mapped to a slot
		 */
		_getDataItem(items, slot, findAll) {
			const slotItems = [];
			items.forEach(function(item) {
				if (item.slotId === slot.getId()){
					slotItems.push(item.item);
				}
			});
			return findAll ? slotItems : slotItems[0];
		}

		/**
		 * Collect the unique values from a category data item
		 */
		_getCategoryDataItemValues(categoryItem, stackIndex) {
			if (stackIndex > -1) {
				let stackValues = {};
				// collect the unique values
				_.each(categoryItem.items, function(item) {
					stackValues[item.t[stackIndex].u] = item.t[stackIndex];
				});

				const keys = Object.keys(stackValues);
				return _.map(keys, function(key) {
					return stackValues[key];
				});
			}
			return [];
		}

		/**
		 * Create a category data item for the multiple measures series
		 */
		_getSeriesDataItemforMultipleMeasures(cats, measures, slot) {
			return {
				itemClass: this._getSeriesDataItemClass(cats, slot),
				items: this._getSeriesTupleItems(cats, measures, slot)
			};
		}

		/**
		 * Collect column ids from data item list of a given slot
		 */
		_getSlotColumnIds(slot) {
			return _.map(slot.getDataItemList(), dataItem => dataItem.getColumnId());
		}

		/**
		 * Collect all series data item class
		 */
		_getSeriesDataItemClass(cats, slot) {
			const ids = this._getSlotColumnIds(slot);
			const stackedItem = this._getDataItem(cats, slot);

			return {
				id: slot.getId(),
				h: _.map(ids, function(id, index) {
					if (SlotAPIHelper.isMultiMeasuresSeriesOrValue(slot, index)) {
						return {
							u: SlotAPIHelper.MULTI_MEASURES_SERIES,
							d: StringResources.get('MeasuresCaption')
						};
					} else {
						if (stackedItem && stackedItem.itemClass) {
							return _.find(stackedItem.itemClass.h, function(header) { return header.u === id; });
						}
						return undefined;
					}
				})
			};
		}

		/**
		 * Collect all series data item tuple items
		 */
		_getSeriesTupleItems(cats, measures, slot) {
			let rows = [];
			let baseRows = [];
			const catItem = this._getDataItem(cats, slot);
			const stackIds = catItem ? _.map(catItem.itemClass.h, function(header) { return header.u; }) : [];
			const ids = this._getSlotColumnIds(slot);

			for( let index = 0; index < ids.length; index++) {
				const id = ids[index];
				const isMultiMeasureSeries = SlotAPIHelper.isMultiMeasuresSeriesOrValue(slot, index);
				let values = isMultiMeasureSeries ? measures : this._getCategoryDataItemValues(catItem, stackIds.indexOf(id));

				//clean up rows and baseRows, so that no defect occurs in the loop with more than 2 ids.
				if (rows.length != 0){
					baseRows = rows;
				}
				rows = [];
				if (baseRows.length > 0) {
					if (values.length > 0) {
						// Should preserve the drop order in multi measure series slot so iterate from baseRows to values
						for( let j=0; j < baseRows.length; j++) {
							const row = baseRows[j];
							// duplicate the values tuple
							let clone = values.slice();
							for( let valueIndex = 0; valueIndex < clone.length; valueIndex++ ) {
								const value = clone[valueIndex];
								let newRow = (valueIndex === 0) ? row : $.extend(true, {}, row);
								newRow.t[index] = isMultiMeasureSeries ? {
									u: this._getMultiMeasureMun(valueIndex),
									d: value.d,
									aggregate: value.aggregate
								} : value;
								rows.push(newRow);
							}
						}
					} else {
						// since there are no data to duplicate, simply update the base rows with null data
						baseRows.forEach( (row) => {
							row.t[index] = {
								u: null,
								d: null
							};
						});
					}
				} else {
					// prepare the base tuple item rows
					baseRows.push.apply(baseRows, _.map(values, (value, index) => {
						return {
							t: [isMultiMeasureSeries ? {
								u: (value && value.u) ? this._getMultiMeasureMun(index) : 'undefined',
								d: (value && value.d) ? value.d: 'undefined',
								aggregate: value.aggregate
							} : value]
						};
					}));
				}
			}

			// finish with the base rows if not expecting a category (i.e. not nested)
			if (!catItem && rows.length === 0) {
				rows.push.apply(rows, baseRows.slice());
			}

			return rows;
		}

		/**
		 * Create a measure data item for the multiple measure values
		 */
		_getValueDataItemForMultipleMeasures(slot) {
			return {
				itemClass: {
					id: slot.getId(),
					h: [{
						u: SlotAPIHelper.MULTI_MEASURES_VALUE,
						d: StringResources.get('ValuesCaption')
					}]
				}
			};
		}

		/**
		 * Process all data point to restructure the data for multiple measures value and series
		 */
		_processDataPoints(dataItems) {
			// prepare the new index for each slot
			let newIndices = this._allocateDataPointIndices(dataItems.extraDataItems);

			// restructured data
			let newData = [];
			let resultData = this._queryResultData;
			let multiMeasureSeriesId = this._getMultiMeasureSeriesId();
			const resultDataData = resultData.data;
			for( let i=0; i<resultDataData.length; i++  ) {
				const datapoint = resultDataData[i];
				this._restructureDataPoint(dataItems, datapoint, newIndices, newData, multiMeasureSeriesId);
			}

			resultData.data = newData;
		}

		/**
		 * Allocate the datapoint index for each data item.
		 * Since the multiple measures are wrapped into a single measure with an extra series for the measures,
		 * None of the slots would have multiple data items.
		 *
		 * ie. category, multimeasure_series, category(series), value
		 * indices: {
		 * 		seriesIndex: 1,
		 * 		valueIndex: 3,
		 * 		catIndices: [0, 2]
		 * }
		 */
		_allocateDataPointIndices(extraDataItems) {
			let catIndex = 0;
			let commonValueIndex = 0;
			const slots = this._getDatasetSlotList();
			let indices = {
				seriesIndex: -1,
				valueIndex: -1,
				commonIndices: [],
				catIndices: []
			};
			slots.forEach((slot, slotIndex) => {
				const isCategory = slot.getDataItemList()[0].getType() === 'attribute';
				if (isCategory) {
					if (SlotAPIHelper.isMultiMeasuresSeriesSlot(slot)) {
						indices.seriesIndex = slotIndex;
					} else {
						indices.catIndices[catIndex++] = slotIndex;
					}
				} else if (SlotAPIHelper.isMultiMeasuresValueSlot(slot)) {
					indices.valueIndex = slotIndex;
				} else {
					slot.getDataItemList().forEach((dataItem, index) => {
						indices.commonIndices[commonValueIndex++] = slotIndex + index;
					});

				}
			});
			extraDataItems.forEach((dataItem) => indices.commonIndices[commonValueIndex++] = dataItem.index);

			return indices;
		}

		/**
		 * Restructure a single data point.
		 * Consolidate multiple measures to one value
		 * Categorize the measures to one category
		 */
		_restructureDataPoint(dataItems, datapoint, indices, newData, multiMeasureSeriesId) {
			let catValueIndex = 0;
			let commonValueIndex = 0;
			let point = [];
			let ordValues = []; // has to be in order
			let catItemInSeries;
			let seriesIndexValue = 0;
			const slots = this._getDatasetSlotList();

			// Prepare the common portion of the data point
			for(let valueIndex =0; valueIndex < datapoint.pt.length; valueIndex++) {
				const value = datapoint.pt[valueIndex];
				let category;
				for( let i = 0; i < dataItems.cats.length; i++) {
					if( dataItems.cats[i].index === valueIndex) {
						category = dataItems.cats[i];
						break;
					}
				}
				if (category) {
					let slot;
					for(let j = 0; j < slots.length; j++) {
						if( slots[j].getId() === category.slotId) {
							slot = slots[j];
							break;
						}
					}

					if (SlotAPIHelper.isMultiMeasuresSeriesSlot(slot)) {
						catItemInSeries = category.item;
						seriesIndexValue = value;
					} else {
						point[indices.catIndices[catValueIndex++]] = value;
					}
				} else if (dataItems.commonIndices[valueIndex]) {
					point[indices.commonIndices[commonValueIndex++]] = value;
				} else if (dataItems.mappedIndices[valueIndex]) {
					// Keep track of the remaining ordinal values (as long as they're 'valid' - mapped to a slot)
					ordValues.push(value);
				}
			}

			// Duplicate the data point by each measure
			const processedIndices = [];
			for(let indexValue = 0; indexValue < ordValues.length; indexValue++) {
				const ordValue = ordValues[indexValue];
				let pt = indexValue === 0 ? point : point.slice();
				pt[indices.seriesIndex] = this._getSeriesIndex(dataItems, indexValue, catItemInSeries, seriesIndexValue, processedIndices, multiMeasureSeriesId);
				pt[indices.valueIndex] = ordValue;
				newData.push({ pt: pt });
				processedIndices.push(indexValue);
			}
		}

		_getSeriesIndex(dataItems, ordIndex, catItemInSeries, originalIndex, processedIndices, multiMeasureSeriesId) {
			// The multiSeriesResultItem has tuples built from _getSeriesTupleItems. It's a cross joined tuple set of the multi measure series
			const multiSeriesResultItem = this._queryResultData && this._queryResultData.dataItems && this._queryResultData.dataItems[QueryResultDataUtils.getDataItemIndex(this._queryResultData, multiMeasureSeriesId)];

			// The target tuple to search in multiSeriesResultItem and return its index
			let measureMun = this._getMultiMeasureMun(ordIndex);
			let targetTuple = catItemInSeries && catItemInSeries.items[originalIndex].t.map( tuple => tuple.u) || [];
			targetTuple.push(measureMun);

			// Search the target tuple in the cross joined tuple set of the multi measure series, and return the index
			let resultIndex = -1;
			if (multiSeriesResultItem.items.length !== 0) {
				for( let idx = 0; idx< multiSeriesResultItem.items.length; idx++ ) {
					const tuple = multiSeriesResultItem.items[idx];
					//check if tuple matches targetTuple & index is not in processedIndices
					let matched = this._isMatched( tuple, targetTuple, processedIndices, idx);
					if (matched) {
						resultIndex = idx;
						break;
					}
				}
			}
			return resultIndex;
		}

		_foundInTarget(u, targetTuple) {
			let found = false;
			for (let j = 0; j < targetTuple.length; j++) {
				if( targetTuple[j] === u) {
					found = true;
					break;
				}
			}
			return found;
		}

		_isMatched(tuple, targetTuple, processedIndices, idx) {
			//check if tuple matches targetTuple & index is not in processedIndices
			const idxContained = processedIndices.includes(idx);
			if( idxContained === false  ) {
				let matches = 0;
				const len = tuple.t.length;
				for (let i = 0; i < len; i++) {
					if( this._foundInTarget( tuple.t[i].u, targetTuple ) ) {
						matches++;
					}
				}
				if( matches === tuple.t.length) {
					return true;
				}
			}

			return false;
		}

		_getMultiMeasureMun(index) {
			if( !this._multiMeasureSlotAPI ) {
				const slotList = this._getDatasetSlotList();
				for( let i = 0; i < slotList.length; i++){
					const slot = slotList[i];
					if( SlotAPIHelper.isMultiMeasuresValueSlot(slot) ) {
						this._multiMeasureSlotAPI = slot;
						break;
					}
				}
			}
			const dataItemAPIs = this._multiMeasureSlotAPI.getDataItemList();
			return dataItemAPIs[index].getId();
		}

		/**
		 * Find the slot ID where the multiMeasureSeries is
		 */
		_getMultiMeasureSeriesId() {
			const slots = this._getDatasetSlotList();
			const multiMeasureSeriesSlot = _.find(slots, (slot) => {
				return SlotAPIHelper.isMultiMeasuresSeriesSlot(slot);
			});
			return multiMeasureSeriesSlot && multiMeasureSeriesSlot.getId();
		}

		_getDatasetSlotList() {
			if(!this._datasetSlotList) {
				const slots = this.visualization.getSlots().getMappedSlotList();
				this._datasetSlotList = slots.filter(slot => {
					return slot.getDefinition().getDatasetIdList().includes(this.queryDefinition.getId());
				});
			}
			return this._datasetSlotList;
		}
	}

	return PostProcessMeasuresAsSeries;
});
